import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import SideBar from '../usercomponents/SideBar';
import TopNavBar from '../usercomponents/TopNavBar';
import Footer from '../usercomponents/Footer';

const apiUrl = process.env.REACT_APP_API_URL;

function MockExamInstructions() {
  const { examId } = useParams();
  const navigate = useNavigate();
  const [exam, setExam] = useState(null);
  const [attemptsRemaining, setAttemptsRemaining] = useState(null);
  const [completedAttempts, setCompletedAttempts] = useState(0);
  const [inProgressAttemptId, setInProgressAttemptId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [starting, setStarting] = useState(false);

  const getToken = () => JSON.parse(sessionStorage.getItem('studentSession'))?.token;

  const fetchInstructions = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = getToken();
      const response = await axios.get(`${apiUrl}/api/student/mock-exams/${examId}`, { headers: { Authorization: `Bearer ${token}` } });
      if (response.data.success) {
        setExam(response.data.exam);
        setAttemptsRemaining(response.data.attempts_remaining);
        setCompletedAttempts(response.data.completed_attempts);
        setInProgressAttemptId(response.data.in_progress_attempt_id);
      } else {
        setError(response.data.message || 'Failed to load exam.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error loading exam.');
    } finally {
      setLoading(false);
    }
  }, [examId]);

  useEffect(() => { fetchInstructions(); }, [fetchInstructions]);

  const handleStart = async () => {
    setError('');
    setStarting(true);
    try {
      const token = getToken();
      const response = await axios.post(`${apiUrl}/api/student/mock-exams/${examId}/attempts`, {}, { headers: { Authorization: `Bearer ${token}` } });
      if (response.data.success) {
        navigate(`/mock-exam-take/${response.data.attemptId}`);
      } else {
        setError(response.data.message || 'Could not start the exam.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error starting exam.');
    } finally {
      setStarting(false);
    }
  };

  if (loading) {
    return (
      <>
        <SideBar />
        <div className="dashboard-main-wrapper"><TopNavBar /><div className="dashboard-body text-center py-40"><div className="spinner-border text-main-600" /></div><Footer /></div>
      </>
    );
  }

  return (
    <>
      <SideBar />
      <div className="dashboard-main-wrapper">
        <TopNavBar />
        <div className="dashboard-body px-3">
          <div className="breadcrumb mb-24">
            <ul className="d-flex align-items-center gap-2 list-unstyled">
              <li><Link to="/mock-exams" className="text-gray-200 text-15 hover-text-main-600 text-decoration-none">Mock Exams</Link></li>
              <li><span className="text-gray-500 d-flex align-items-center"><i className="ph ph-caret-right" /></span></li>
              <li><span className="text-main-600 text-15">Instructions</span></li>
            </ul>
          </div>

          {error && <div className="alert alert-danger mb-20">{error}</div>}

          {exam && (
            <div className="card shadow-sm border-0">
              <div className="card-body p-24">
                <h4 className="fw-bold mb-4">{exam.title}</h4>
                <div className="text-gray-500 mb-20">{exam.coursename}</div>

                {exam.description && <div className="mb-20" dangerouslySetInnerHTML={{ __html: exam.description }} />}
                {exam.instructions && (
                  <div className="mb-20 p-16" style={{ background: '#f6faf8', borderRadius: 12 }} dangerouslySetInnerHTML={{ __html: exam.instructions }} />
                )}

                <div className="row g-16 mb-24">
                  <div className="col-sm-4 col-6"><div className="text-13 text-gray-400">Questions</div><div className="fw-semibold">{exam.total_questions}</div></div>
                  <div className="col-sm-4 col-6"><div className="text-13 text-gray-400">Total Marks (approx.)</div><div className="fw-semibold">{exam.estimated_total_marks}</div></div>
                  <div className="col-sm-4 col-6"><div className="text-13 text-gray-400">Pass Percentage</div><div className="fw-semibold">{exam.pass_percentage}%</div></div>
                  <div className="col-sm-4 col-6"><div className="text-13 text-gray-400">Duration</div><div className="fw-semibold">{exam.duration_minutes ? `${exam.duration_minutes} minutes` : 'Untimed'}</div></div>
                  <div className="col-sm-4 col-6"><div className="text-13 text-gray-400">Attempt Limit</div><div className="fw-semibold text-capitalize">{exam.attempt_policy === 'limited' ? `${exam.max_attempts} attempts` : exam.attempt_policy}</div></div>
                  <div className="col-sm-4 col-6"><div className="text-13 text-gray-400">Negative Marking</div><div className="fw-semibold">{exam.negative_marking_enabled ? 'Yes' : 'No'}</div></div>
                  <div className="col-sm-4 col-6"><div className="text-13 text-gray-400">Attempts Used</div><div className="fw-semibold">{completedAttempts}{attemptsRemaining !== null ? ` / ${completedAttempts + attemptsRemaining}` : ''}</div></div>
                  <div className="col-sm-4 col-6"><div className="text-13 text-gray-400">Answer Review</div><div className="fw-semibold">{exam.show_correct_answers ? 'Allowed' : 'Not shown'}</div></div>
                </div>

                {inProgressAttemptId ? (
                  <button type="button" className="btn btn-main rounded-pill py-11 px-32" onClick={() => navigate(`/mock-exam-take/${inProgressAttemptId}`)}>
                    Continue Exam
                  </button>
                ) : attemptsRemaining === 0 ? (
                  <div className="alert alert-warning mb-0">You have used all available attempts for this exam.</div>
                ) : (
                  <button type="button" className="btn btn-main rounded-pill py-11 px-32" onClick={handleStart} disabled={starting}>
                    {starting ? 'Starting...' : 'Start Exam'}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
        <Footer />
      </div>
    </>
  );
}

export default MockExamInstructions;
